import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { SquarePlus, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { IItemList } from '@/pages/ItemEyes/api/ItemEyes.api';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { AddOneItemToRequest } from '@/pages/PurchasesContainer/RequestPerOffer/api/OffersRequest.api';
import { AutoCompleteItems } from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/itemsTable/UpdateItemDialog/AllItemsSearch';

type Props = {
  allItems: IOneItemInPrepareForRequest[];
  handleAddOneItemLink: (item: IOneItemInPrepareForRequest) => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
  offerID: string;
};

function AddItemToRequestDialog({
  offerID,
  allItems,
  handleAddOneItemLink,
  handleLoadingStage,
  isLoading,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [itemData, setItemData] = useState<null | IItemList>(null);
  const [discount, setDiscount] = useState<number>(20);

  const handleItemData = useCallback((newValue: IItemList | null) => {
    setItemData(newValue);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setItemData(null);
      setDiscount(20);
    }
  }, []);

  const handleDiscountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(parseFloat(e.target.value).toFixed(2));
      if (!isNaN(value)) {
        setDiscount(value);
      }
    },
    [],
  );

  const addItemLinkMutation = useMutation({
    mutationFn: AddOneItemToRequest,
    onSuccess: (data) => {
      setItemData(null);
      handleAddOneItemLink(data);
      setIsOpen(false);
      handleLoadingStage(false, 'update');
      toast.success('🎉 الصنف دخل السيستم! شاطر 😎⚡', {
        position: 'bottom-center',
        style: {
          background: '#10B981',
          color: '#FFFFFF',
          fontWeight: 'bold',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
        },
      });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      handleLoadingStage(false, 'update');

      const errorMessage =
        err?.response?.data?.message || 'حدث خطأ أثناء إضافة الصنف';

      toast.error(errorMessage, {
        position: 'bottom-center',
        style: {
          background: '#EF4444',
          color: '#FFFFFF',
          fontWeight: 'bold',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
        },
      });
    },
  });

  const handleAddItem = useCallback(async () => {
    if (!itemData) {
      return;
    }

    if (allItems.some((item) => item.HealthyItemID === itemData?.ID)) {
      toast.error('🤔 الصنف موجود بالفعل في القائمة', {
        position: 'bottom-center',
        style: {
          background: '#EF4444',
          color: '#FFFFFF',
          fontWeight: 'bold',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
        },
      });
      return;
    }

    if (itemData?.ID) {
      try {
        handleLoadingStage(true, 'update');
        await addItemLinkMutation.mutateAsync({
          id: offerID,
          itemID: itemData.ID,
          discount,
        });
      } catch (error) {
        // Error is already handled in the mutation's onError callback
        // This catch block ensures the component doesn't crash if an error occurs
      }
    }
  }, [
    itemData,
    allItems,
    handleLoadingStage,
    offerID,
    discount,
    addItemLinkMutation,
  ]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className={cn(
            'group relative flex h-9 items-center gap-2 px-4 py-0 text-sm font-medium transition-all duration-200',
            'border-indigo/20 border-2 bg-white/80 shadow-sm backdrop-blur-sm',
            'hover:border-indigo/40 hover:bg-white/90 hover:shadow-md',
            'dark:border-indigo/30 dark:hover:border-indigo/50 dark:bg-gray-800/80 dark:hover:bg-gray-800/90',
            'focus:ring-indigo/20 focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
          )}
          variant="outline"
        >
          <SquarePlus className="h-4 w-4 text-indigo-600 transition-colors group-hover:text-indigo-700 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
          <span className="font-semibold text-indigo-700 transition-colors group-hover:text-indigo-800 dark:text-indigo-300 dark:group-hover:text-indigo-200">
            اضافة صنف
          </span>

          {/* Enhanced animated background */}
          <span className="from-indigo/5 via-indigo/10 to-indigo/5 dark:from-indigo/10 dark:via-indigo/20 dark:to-indigo/10 absolute inset-0 z-0 rounded-md bg-gradient-to-r opacity-0 transition-all duration-300 group-hover:opacity-100"></span>
        </Button>
      </DialogTrigger>

      <DialogContent
        className="overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 p-0 shadow-xl dark:from-gray-900 dark:to-gray-900/95"
        size="lg"
        hiddenCloseIcon
        overlayClass="backdrop-blur-sm dark:from-gray-500/20 dark:to-primary/10 bg-gradient-to-b from-background/10 to-primary/5"
      >
        <DialogTitle className="w-full bg-gradient-to-r from-indigo-50 to-indigo-100/30 p-4 text-xl dark:from-indigo-950/20 dark:to-indigo-900/10">
          <div className="flex w-full items-center justify-start">
            <div className="group relative flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 p-2 shadow-lg shadow-indigo-500/20 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/30 dark:from-indigo-600 dark:to-indigo-700 dark:shadow-indigo-700/20 dark:hover:shadow-indigo-700/30">
              <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-gradient-to-br from-indigo-400/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <div className="absolute -bottom-1 -right-1 h-2 w-2 rounded-full bg-gradient-to-tl from-indigo-400/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <SquarePlus className="h-8 w-8 text-white" />
            </div>
            <div className="ml-4">
              <div className="p-2">
                <span className="text-xl font-bold text-foreground">
                  اضافة صنف جديد
                </span>
                <div className="flex items-center gap-1">
                  <DialogDescription className="text-muted-foreground">
                    اضافة صنف جديد للطلب بشرط ان لا يكون موجود في الاصناف
                  </DialogDescription>
                </div>
              </div>
            </div>
          </div>

          <DialogClose className="absolute left-3 top-3" asChild>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full border-primary/20 bg-background/50 text-primary hover:bg-primary/10 hover:text-primary dark:border-primary/30 dark:bg-background/30 dark:hover:bg-primary/20"
              onClick={() => {
                setItemData(null);
                setIsOpen(false);
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogClose>
          <Separator className="my-2" />
        </DialogTitle>

        <div className="flex flex-col gap-4 p-4 px-6">
          <div className="rounded-lg border border-indigo-200/30 bg-white p-4 shadow-sm transition-all duration-300 hover:border-indigo-300/40 hover:shadow-md dark:border-indigo-700/30 dark:bg-gray-800/40 dark:hover:border-indigo-600/40">
            <Badge className="mb-2 rounded-md bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
              اسم الصنف
            </Badge>
            <div className="z-50 flex flex-col gap-2">
              <AutoCompleteItems
                onValueChange={handleItemData}
                value={itemData}
                searchPlaceholder="ابحث عن صنف"
              />
            </div>
          </div>

          <div className="rounded-lg border border-indigo-200/30 bg-white p-4 shadow-sm transition-all duration-300 hover:border-indigo-300/40 hover:shadow-md dark:border-indigo-700/30 dark:bg-gray-800/40 dark:hover:border-indigo-600/40">
            <Badge className="mb-2 rounded-md bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
              الخصم
            </Badge>
            <div className="p-2">
              <Input
                type="number"
                value={discount}
                onChange={handleDiscountChange}
                className="text-center text-lg font-semibold"
                min="0"
                max="100"
                step="0.01"
              />
              <Label className="mt-1 block text-center text-sm text-muted-foreground">
                نسبة الخصم (٪)
              </Label>
            </div>
          </div>

          {itemData && (
            <div className="rounded-lg border border-green-200/30 bg-green-50/50 p-4 shadow-sm dark:border-green-700/30 dark:bg-green-900/10">
              <Badge className="mb-2 rounded-md bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/50 dark:text-green-300">
                تفاصيل الصنف المختار
              </Badge>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    اسم الصنف:
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {itemData.Name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    السعر:
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {itemData.Price} ج.م
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    السعر بعد الخصم:
                  </span>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {(itemData.Price * (1 - discount / 100)).toFixed(2)} ج.م
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-3 border-t bg-gray-50/50 px-6 py-4 dark:bg-gray-800/50">
          <Button
            variant="outline"
            onClick={() => {
              setItemData(null);
              setIsOpen(false);
            }}
            className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            إلغاء
          </Button>
          <Button
            onClick={handleAddItem}
            disabled={!itemData || isLoading}
            className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800 disabled:opacity-50"
          >
            {isLoading ? 'جاري الإضافة...' : 'إضافة الصنف'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddItemToRequestDialog;
